import { type ReactNode } from "react";

import clsx from "clsx";

import { type TileDescriptor } from "@features/metrics-explorer/types";
import { useFeatureFlags } from "@hooks/http";

import { type MetricsExplorerCorrelationRow } from "../metrics/correlation-utils";

import { CorrelationPill } from "./correlation-pill";
import { GradientBar } from "./gradient-bar";

interface Props {
  tile: TileDescriptor;
  correlation?: MetricsExplorerCorrelationRow;
  onOpenTooltip(): void;
  onCloseTooltip(): void;
}

export const CorrelationRow = ({
  tile,
  correlation,
  onOpenTooltip,
  onCloseTooltip,
}: Props) => {
  const { hasFeatureFlag } = useFeatureFlags();

  if (
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.active &&
    tile.index < 2
  ) {
    return (
      <div
        className={clsx(
          "h-[49px] text-xs bg-cosmic text-white flex items-center justify-center px-3",
        )}
      >
        Upgrade to see all correlations
      </div>
    );
  }

  if (!correlation) {
    return <div className="h-[49px]"></div>;
  }

  if (correlation && !correlation.canBeCalculated) {
    return (
      <div className="h-[49px] text-[11px] leading-4 bg-pluto-1 px-3 flex items-center">
        Metrics have different date range lengths - analysis not possible
      </div>
    );
  }

  if (tile.active) {
    return (
      <div
        className={clsx(
          "h-[49px] text-xs bg-cosmic text-white flex items-center justify-center",
        )}
      >
        Showing correlation to this metric
      </div>
    );
  }

  const r =
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.index > 1
      ? 0
      : correlation.r;

  let correlationText = isNaN(r) ? (
    <>NO</>
  ) : (
    <>
      {r > 0 ? "+" : ""}
      {r.toFixed(2)}
    </>
  );

  if (
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.index > 1
  ) {
    correlationText = <span className="blur-sm">+0.00</span>;
  }

  let correlationPill: ReactNode = null;

  if (!isNaN(r)) {
    correlationPill = (
      <CorrelationPill
        r={r}
        onOpenTooltip={onOpenTooltip}
        onCloseTooltip={onCloseTooltip}
      />
    );
  }

  if (
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.index === 2
  ) {
    const body =
      "Upgrade to unlock additional correlation metrics in Metrics Explorer";

    correlationPill = (
      <a
        className={clsx(
          "font-bold text-[9px] px-2.5 rounded-full whitespace-nowrap leading-6",
          "bg-cosmic no-underline text-white hover:bg-cosmic+1",
        )}
        href={`mailto:sales@northbeam.io?subject=${body}&body=${body}`}
      >
        UPGRADE FOR ACCESS
      </a>
    );
  }

  if (
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.index > 2
  ) {
    correlationPill = <span className="h-6 w-1 bg-v2-primary"></span>;
  }

  return (
    <div className="h-[49px] px-3 pb-2 flex flex-col justify-center gap-1.5">
      <GradientBar value={isNaN(r) ? 0 : r} />
      <div className="flex items-center justify-between">
        <div className="font-bold text-xxs uppercase whitespace-nowrap text-v2-secondary">
          {correlationText} CORRELATION
        </div>
        {correlationPill}
      </div>
    </div>
  );
};
